import type { ReactNode } from "react";
import { useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { useLink } from "solito/link";
import { type GetProps, styled, XStack, YStack } from "tamagui";
import { CalendarText } from "../Formatting";
import { Body1, Body2 } from "../Typography";
const StyledListItem = styled(XStack, {
  name: "ListItem",
  alignItems: "center",
  gap: "$sp1.5",
  paddingVertical: "$sp1.5",
  paddingHorizontal: "$sp2",
  hoverStyle: {
    backgroundColor: "$surface1Hover"
  },
  pressStyle: {
    backgroundColor: "$surface1Hover"
  },
  variants: {
    selected: {
      true: {
        backgroundColor: "$surface0"
      },
      false: {
        backgroundColor: "$transparent"
      }
    },
    interactive: {
      link: {
        tag: "a",
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer"
      },
      button: {
        tag: "button",
        cursor: "pointer"
      },
      unset: {}
    }
  } as const,
  defaultVariants: {
    selected: false,
    interactive: "unset"
  } as const
});
type StyledListItemProps = GetProps<typeof StyledListItem>;
const MetadataText = styled(Body2, {
  color: "$textSubtle",
  flexShrink: 0,
  numberOfLines: 1
});
type MetadataProps = {
  metadata?: string;
  metadataDate?: never;
} | {
  metadata?: never;
  metadataDate?: string | Date | null;
};
type DataBadgeProps = {
  data?: string;
  badge?: never;
} | {
  data?: never;
  badge?: ReactNode;
};
export type ListItemProps = StyledListItemProps & MetadataProps & DataBadgeProps & {
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  title: string;
  subtitle?: string;
  children?: never;
  href?: string;
};
export const ListItem = ({
  leftElement,
  rightElement,
  title,
  data,
  badge,
  subtitle,
  metadata,
  metadataDate,
  href,
  onPress,
  ...rest
}: ListItemProps) => {
  const linkResult = useLink({
    href: href || "/"
  });
  const linkProps = href ? linkResult : {};
  const mergedOnPress = useCallback((e: GestureResponderEvent) => {
    onPress?.(e);
    linkResult.onPress?.(e);
  }, [onPress, linkResult.onPress]);
  const finalLinkProps = href ? {
    ...linkProps,
    onPress: mergedOnPress
  } : onPress ? {
    onPress
  } : {};
  const interactive = href ? "link" : onPress ? "button" : "unset";
  const hasMetadata = metadata || metadataDate;
  const hasBottomRow = subtitle || hasMetadata;
  return <StyledListItem interactive={interactive} {...finalLinkProps} {...rest} data-sentry-element="StyledListItem" data-sentry-component="ListItem" data-sentry-source-file="ListItem.tsx">
      {leftElement ? leftElement : null}

      <YStack flex={1} gap="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="ListItem.tsx">
        <XStack justifyContent="space-between" alignItems="center" gap="$sp1.5"
      // need to make height fixed because badge's are taller than body1
      height="$si3" data-sentry-element="XStack" data-sentry-source-file="ListItem.tsx">
          <Body1 color="$textDefault" flexShrink={1} numberOfLines={1} data-sentry-element="Body1" data-sentry-source-file="ListItem.tsx">
            {title}
          </Body1>
          {badge ? badge : data ? <Body1 color="$textDefault" flexShrink={0} numberOfLines={1}>
              {data}
            </Body1> : null}
        </XStack>
        {hasBottomRow ? <XStack justifyContent="space-between" alignItems="center" gap="$sp1.5">
            {subtitle ? <Body2 color="$textSubtle" flexShrink={1} numberOfLines={1}>
                {subtitle}
              </Body2> : null}
            {metadataDate ? <CalendarText date={metadataDate} element={MetadataText} /> : metadata ? <MetadataText>{metadata}</MetadataText> : null}
          </XStack> : null}
      </YStack>

      {rightElement ? rightElement : null}
    </StyledListItem>;
};